import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createInvoice, updateInvoice, fetchCompanies, fetchPlacements } from '@/lib/api';
import type { Invoice } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InvoiceLineItems } from './InvoiceLineItems';
import { formatCurrency } from '@/lib/utils';

interface InvoiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  companyId?: string;
  placementId?: string;
  /** When provided, the form enters edit mode */
  editInvoice?: Invoice | null;
}

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

export function InvoiceForm({
  open,
  onOpenChange,
  onSuccess,
  companyId: initialCompanyId,
  placementId: initialPlacementId,
  editInvoice,
}: InvoiceFormProps) {
  const isEditing = !!editInvoice;
  const queryClient = useQueryClient();

  const [companyId, setCompanyId] = useState(initialCompanyId || '');
  const [placementId, setPlacementId] = useState(initialPlacementId || '');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState<string>('ZAR');
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: '', quantity: 1, unitPrice: 0 },
  ]);

  // Reset form when dialog opens — pre-fill in edit mode
  useEffect(() => {
    if (open) {
      if (editInvoice) {
        setCompanyId(editInvoice.companyId);
        setPlacementId(editInvoice.placementId || '');
        setDueDate(editInvoice.dueDate ? editInvoice.dueDate.split('T')[0] : '');
        setNotes(editInvoice.notes || '');
        setCurrency(editInvoice.currency || 'ZAR');
        setLineItems(
          editInvoice.lineItems.map((li) => ({
            description: li.description,
            quantity: li.quantity,
            unitPrice: li.unitPrice ?? 0,
          })),
        );
      } else {
        setCompanyId(initialCompanyId || '');
        setPlacementId(initialPlacementId || '');
        setDueDate('');
        setNotes('');
        setCurrency('ZAR');
        setLineItems([{ description: '', quantity: 1, unitPrice: 0 }]);
      }
    }
  }, [open, initialCompanyId, initialPlacementId, editInvoice]);

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery({
    queryKey: ['companies-all'],
    queryFn: () => fetchCompanies(),
    enabled: open,
  });

  // Fetch placements for dropdown (filtered by company if selected)
  const { data: placementsData } = useQuery({
    queryKey: ['placements-for-invoice', companyId],
    queryFn: () => fetchPlacements({ companyId: companyId || undefined, limit: 50 }),
    enabled: open,
  });

  const placements = placementsData?.data ?? [];

  // Calculate totals
  const { subtotal, vat, total } = useMemo(() => {
    const sub = lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    const vatAmount = sub * 0.15;
    return {
      subtotal: sub,
      vat: vatAmount,
      total: sub + vatAmount,
    };
  }, [lineItems]);

  // Auto-populate line items when a placement is selected
  useEffect(() => {
    if (placementId) {
      const placement = placements.find((p) => p.id === placementId);
      if (placement && placement.feeAmount != null) {
        setLineItems([
          {
            description: `Recruitment fee - ${placement.candidate.firstName} ${placement.candidate.lastName} (${placement.job.title})`,
            quantity: 1,
            unitPrice: placement.feeAmount,
          },
        ]);
        // Also set the company from the placement
        if (placement.companyId) {
          setCompanyId(placement.companyId);
        }
      }
    }
  }, [placementId, placements]);

  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof createInvoice>[0]) => isEditing ? updateInvoice(editInvoice!.id, data) : createInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      if (isEditing) {
        queryClient.invalidateQueries({ queryKey: ['invoice', editInvoice!.id] });
      }
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId) return;

    const validItems = lineItems.filter(
      (item) => item.description.trim() && item.quantity > 0 && item.unitPrice > 0,
    );

    if (validItems.length === 0) return;

    mutation.mutate({
      companyId,
      placementId: placementId || undefined,
      dueDate: dueDate || undefined,
      notes: notes || undefined,
      currency,
      lineItems: validItems,
    });
  };

  const hasValidItems = lineItems.some(
    (item) => item.description.trim() && item.quantity > 0 && item.unitPrice > 0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Invoice</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company & Placement */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Company *</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="placement">Linked Placement (optional)</Label>
              <Select
                value={placementId}
                // Radix Select items cannot carry an empty value, so "None" is
                // the sentinel 'none' — it must map back to '' here or the
                // submit would send placementId: 'none' as a real id.
                onValueChange={(value) => setPlacementId(value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select placement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {placements.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.candidate.firstName} {p.candidate.lastName} - {p.job.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {companyId && placements.length === 0 && (
                <p className="text-xs text-text-tertiary">
                  No placements recorded for this company yet. Placements are created
                  automatically when a candidate is marked Hired in the pipeline, or manually
                  on the Placements page.
                </p>
              )}
            </div>
          </div>

          {/* Due Date & Currency */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-[240px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger id="currency" className="w-[240px]">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ZAR">ZAR — South African Rand</SelectItem>
                  <SelectItem value="USD">USD — US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR — Euro</SelectItem>
                  <SelectItem value="GBP">GBP — British Pound</SelectItem>
                  <SelectItem value="AUD">AUD — Australian Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-2">
            <Label>Line Items *</Label>
            <InvoiceLineItems items={lineItems} onChange={setLineItems} currency={currency} />
          </div>

          {/* Totals Summary */}
          <div className="bg-bg-elevated rounded-lg border border-white/10 p-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-tertiary">Subtotal</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(subtotal, currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-tertiary">VAT (15%)</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(vat, currency)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/10">
                <span className="text-text-primary font-semibold text-base">Total</span>
                <span className="text-emerald-500 font-bold text-lg">
                  {formatCurrency(total, currency)}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Payment terms, bank details, or other notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Footer */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!companyId || !hasValidItems || mutation.isPending}
            >
              {mutation.isPending ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Invoice')}
            </Button>
          </DialogFooter>

          {mutation.isError && (
            <p className="text-sm text-red-500 mt-2">
              {(mutation.error as Error)?.message || isEditing ? 'Failed to update invoice' : 'Failed to create invoice'}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
